import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
  pointerWithin
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Filter,
  Search,
  RefreshCw,
  X,
  XCircle,
  Timer,
  User,
  Plus
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { DeliverableKanbanCard } from '@/components/deliverables/DeliverableKanbanCard';
import { WonLeadsAlert } from '@/components/deliverables/WonLeadsAlert';
import { AddDeliverableModal } from '@/components/deliverables/AddDeliverableModal';
import { Deliverable, DeliverableStatus } from '@/types/deliverables';
import { User as UserType } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Define Kanban columns
const KANBAN_COLUMNS: { status: DeliverableStatus; label: string; color: string; bgColor: string; icon: React.ReactNode }[] = [
  {
    status: 'PENDING',
    label: 'Pending',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    icon: <Clock className="w-4 h-4" />
  },
  {
    status: 'IN_PROGRESS',
    label: 'In Progress',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: <RefreshCw className="w-4 h-4" />
  },
  {
    status: 'DELIVERED',
    label: 'Delivered',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    icon: <Package className="w-4 h-4" />
  },
  {
    status: 'VERIFIED',
    label: 'Verified',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    icon: <CheckCircle2 className="w-4 h-4" />
  },
  {
    status: 'REJECTED',
    label: 'Rejected',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: <XCircle className="w-4 h-4" />
  },
  {
    status: 'DELAYED',
    label: 'Delayed',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    icon: <Timer className="w-4 h-4" />
  }
];

// Droppable Column Component
const DroppableColumn: React.FC<{
  status: DeliverableStatus;
  deliverables: Deliverable[];
  column: typeof KANBAN_COLUMNS[0];
  onCardClick: (deliverable: Deliverable) => void;
}> = ({ status, deliverables, column, onCardClick }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status
  });

  const totalValue = deliverables.reduce((sum, d) => sum + (d.lead?.dealValue || 0), 0);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col h-full min-h-[600px] rounded-lg transition-all duration-200',
        isOver ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-gray-50'
      )}
    >
      {/* Column Header */}
      <div className={cn('p-4 rounded-t-lg', column.bgColor)}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className={column.color}>{column.icon}</span>
            <h3 className={cn('font-semibold', column.color)}>{column.label}</h3>
          </div>
          <span className={cn(
            'px-2 py-0.5 rounded-full text-sm font-medium',
            column.bgColor,
            column.color
          )}>
            {deliverables.length}
          </span>
        </div>
        {totalValue > 0 && (
          <p className="text-sm text-gray-600">
            Total: ₹{totalValue.toLocaleString('en-IN')}
          </p>
        )}
      </div>

      {/* Column Content */}
      <div className="flex-1 p-3 overflow-y-auto space-y-3">
        <SortableContext items={deliverables.map(d => d.id)} strategy={verticalListSortingStrategy}>
          <AnimatePresence>
            {deliverables.map((deliverable) => (
              <DeliverableKanbanCard
                key={deliverable.id}
                deliverable={deliverable}
                onClick={onCardClick}
              />
            ))}
          </AnimatePresence>
        </SortableContext>

        {/* Empty State */}
        {deliverables.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Package className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">No deliverables yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Filter State Interface
interface FilterState {
  assignedTo: string;
  category: string;
  dueDateFrom: string;
  dueDateTo: string;
  search: string;
}

export const DeliverableKanbanPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    assignedTo: '',
    category: 'ALL',
    dueDateFrom: '',
    dueDateTo: '',
    search: ''
  });

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  // Fetch deliverables for Kanban view
  const { data: kanbanData, isLoading, error } = useQuery({
    queryKey: ['deliverables-kanban', filters],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (filters.assignedTo) params.append('assignedToId', filters.assignedTo);
      if (filters.category && filters.category !== 'ALL') params.append('category', filters.category);
      if (filters.dueDateFrom) params.append('dueDateFrom', filters.dueDateFrom);
      if (filters.dueDateTo) params.append('dueDateTo', filters.dueDateTo);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${API_BASE_URL}/deliverables/kanban?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Fallback to regular deliverables endpoint if kanban endpoint doesn't exist
        if (response.status === 404) {
          const fallbackResponse = await fetch(`${API_BASE_URL}/deliverables?${params}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (!fallbackResponse.ok) throw new Error('Failed to fetch deliverables');
          const result = await fallbackResponse.json();
          const deliverables: Deliverable[] = result.data || result;

          // Group by status
          return {
            deliverables,
            stats: {
              total: deliverables.length,
              overdue: deliverables.filter(d =>
                ['PENDING', 'IN_PROGRESS'].includes(d.status) &&
                new Date(d.committedDate) < new Date()
              ).length,
              dueThisWeek: deliverables.filter(d => {
                if (['DELIVERED', 'VERIFIED'].includes(d.status)) return false;
                const dueDate = new Date(d.committedDate);
                const today = new Date();
                const weekFromNow = new Date();
                weekFromNow.setDate(today.getDate() + 7);
                return dueDate >= today && dueDate <= weekFromNow;
              }).length
            }
          };
        }
        throw new Error('Failed to fetch deliverables');
      }

      const result = await response.json();
      return result.data || result;
    }
  });

  // Fetch users for filter dropdown
  const { data: users = [] } = useQuery<UserType[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const result = await response.json();
      return result.data || result;
    }
  });

  // BUG-54 fix: Proper optimistic update with rollback via onMutate/onError/onSettled.
  // BUG-66 fix: Captures queryKey (including filters) at mutation time, preventing
  // filter-change race conditions during drag.
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: DeliverableStatus }) => {
      const token = localStorage.getItem('authToken');
      const updateData: any = { status };

      // Automatically set delivered date when moved to DELIVERED
      if (status === 'DELIVERED') {
        updateData.deliveredDate = new Date().toISOString();
      }

      const response = await fetch(`${API_BASE_URL}/deliverables/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        // Fallback to regular update endpoint
        const fallbackResponse = await fetch(`${API_BASE_URL}/deliverables/${id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });
        if (!fallbackResponse.ok) throw new Error('Failed to update status');
        return fallbackResponse.json();
      }
      return response.json();
    },
    onMutate: async ({ id, status }) => {
      // Capture the exact query key at mutation time (includes current filters)
      const queryKey = ['deliverables-kanban', filters];

      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update the cache
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          deliverables: old.deliverables?.map((d: Deliverable) =>
            d.id === id ? { ...d, status } : d
          )
        };
      });

      // Return context with snapshot for rollback
      return { previousData, queryKey };
    },
    onError: (error: Error, _variables, context) => {
      // Restore from snapshot on failure
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      toast({
        title: 'Update Failed',
        description: error.message,
        variant: 'destructive'
      });
    },
    onSuccess: () => {
      toast({
        title: 'Status Updated',
        description: 'Deliverable status has been updated'
      });
    },
    onSettled: (_data, _error, _variables, context) => {
      // Always refetch after mutation to ensure server state is in sync
      queryClient.invalidateQueries({ queryKey: context?.queryKey || ['deliverables-kanban'] });
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
    }
  });

  // Process deliverables data
  const deliverables: Deliverable[] = useMemo(() => {
    if (!kanbanData) return [];
    if (kanbanData.deliverables) return kanbanData.deliverables;
    return Object.entries(kanbanData.columns || {}).flatMap(([status, items]) =>
      (Array.isArray(items) ? items : []).map((it: any) => ({ ...it, status: it.status ?? status }))
    );
  }, [kanbanData]);

  // Filter deliverables by search
  const filteredDeliverables = useMemo(() => {
    if (!filters.search) return deliverables;
    const searchLower = filters.search.toLowerCase();
    return deliverables.filter(d =>
      d.title.toLowerCase().includes(searchLower) ||
      d.lead?.name.toLowerCase().includes(searchLower) ||
      d.lead?.company.toLowerCase().includes(searchLower)
    );
  }, [deliverables, filters.search]);

  // Group deliverables by status
  const deliverablesByStatus = useMemo(() => {
    const grouped: Record<DeliverableStatus, Deliverable[]> = {
      PENDING: [],
      IN_PROGRESS: [],
      DELIVERED: [],
      VERIFIED: [],
      REJECTED: [],
      DELAYED: []
    };

    filteredDeliverables.forEach(d => {
      if (grouped[d.status]) {
        grouped[d.status].push(d);
      }
    });

    return grouped;
  }, [filteredDeliverables]);

  // Stats
  const stats = kanbanData?.stats || {
    total: deliverables.length,
    overdue: 0,
    dueThisWeek: 0
  };

  // Drag Handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const deliverableId = active.id as string;
    const newStatus = over.id as DeliverableStatus;

    // Find the deliverable
    const deliverable = deliverables.find(d => d.id === deliverableId);
    if (!deliverable || deliverable.status === newStatus) return;

    // BUG-54: Optimistic update is now handled in onMutate, not here.
    // This ensures proper snapshot/rollback and avoids race conditions (BUG-66).
    updateStatusMutation.mutate({ id: deliverableId, status: newStatus });
  };

  // Card click handler
  const handleCardClick = (deliverable: Deliverable) => {
    if (deliverable.leadId) {
      navigate(`/clients/${deliverable.leadId}/deliverables`);
    }
  };

  // Assign deliverables to a lead
  const handleAssignDeliverables = (leadId: string) => {
    setSelectedLeadId(leadId);
    setShowAddModal(true);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      assignedTo: '',
      category: 'ALL',
      dueDateFrom: '',
      dueDateTo: '',
      search: ''
    });
  };

  const hasActiveFilters = filters.assignedTo || (filters.category && filters.category !== 'ALL') || filters.dueDateFrom || filters.dueDateTo;

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load deliverables</h3>
          <p className="text-gray-600 mb-4">Please try refreshing the page</p>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['deliverables-kanban'] })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            aria-label="Retry loading deliverables"
          >
            Retry
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Deliverables Kanban</h1>
            <p className="text-gray-600 mt-1">
              Drag and drop to update deliverable status
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedLeadId(undefined);
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" aria-hidden="true" />
            Add Deliverable
          </button>
        </div>

        {/* Stats Header */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Deliverables</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overdue</p>
                <p className={cn(
                  'text-2xl font-bold mt-1',
                  stats.overdue > 0 ? 'text-red-600' : 'text-gray-900'
                )}>
                  {stats.overdue}
                </p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Due This Week</p>
                <p className={cn(
                  'text-2xl font-bold mt-1',
                  stats.dueThisWeek > 0 ? 'text-yellow-600' : 'text-gray-900'
                )}>
                  {stats.dueThisWeek}
                </p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Won Leads Alert */}
        <WonLeadsAlert onAssignDeliverables={handleAssignDeliverables} />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search deliverables..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors',
                showFilters || hasActiveFilters
                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              )}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 bg-blue-500 rounded-full" />
              )}
            </button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {/* Expanded Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
                  {/* Assigned To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To
                    </label>
                    <select
                      value={filters.assignedTo}
                      onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Users</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ALL">All Categories</option>
                      <option value="PROPOSAL">Proposal</option>
                      <option value="CONTRACT">Contract</option>
                      <option value="IMPLEMENTATION">Implementation</option>
                      <option value="INSTALLATION">Installation</option>
                      <option value="TRAINING">Training</option>
                      <option value="SUPPORT">Support</option>
                      <option value="DOCUMENTATION">Documentation</option>
                      <option value="LICENSE">License</option>
                      <option value="SERVICE">Service</option>
                      <option value="EQUIPMENT">Equipment</option>
                      <option value="CONSULTATION">Consultation</option>
                      <option value="MAINTENANCE">Maintenance</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* Due Date From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date From
                    </label>
                    <input
                      type="date"
                      value={filters.dueDateFrom}
                      onChange={(e) => setFilters({ ...filters, dueDateFrom: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Due Date To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date To
                    </label>
                    <input
                      type="date"
                      value={filters.dueDateTo}
                      onChange={(e) => setFilters({ ...filters, dueDateTo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Kanban Board */}
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {KANBAN_COLUMNS.map((column) => (
                <DroppableColumn
                  key={column.status}
                  status={column.status}
                  column={column}
                  deliverables={deliverablesByStatus[column.status]}
                  onCardClick={handleCardClick}
                />
              ))}
            </div>

            <DragOverlay>
              {activeId && (
                <div className="bg-white rounded-lg shadow-xl border-2 border-blue-400 p-4 rotate-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">Moving deliverable...</span>
                  </div>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Add Deliverable Modal */}
      {showAddModal && (
        <AddDeliverableModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setSelectedLeadId(undefined);
          }}
          preselectedLeadId={selectedLeadId}
        />
      )}
    </DashboardLayout>
  );
};
